import { useAtom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import { append } from 'ramda'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useModalState } from '~/atoms/ModalState'
import { StorageKeys } from '~/atoms/StorageKeys'
import { useUserState } from '~/atoms/UserState'
import { useAnalytics } from '~/hooks/useAnalytics'
import {
  useBlockNumberQuery,
  useGetBlockedNumbersQuery,
  useUnblockNumberQuery,
} from '~/queries/blockNumberQueries'
import Logger from '~/services/Logger'
import { AnalyticsNamespace } from '~/types/Analytics'
import { SharedAnalyticsEvents } from '~/types/Analytics/SharedAnalyticsEvents'
import { phoneFormatter } from '~/utils/formatter'
import { showErrorToast } from '~/utils/toast'

export interface BlockedNumber {
  phoneNumber: string
  id: number
}

interface BlockedNumbersStorage {
  blocked: BlockedNumber[] | null | undefined
}

const blockedNumbersAtom = atomWithStorage<BlockedNumbersStorage>(
  StorageKeys.BlockedNumbers,
  {
    blocked: null,
  },
)

/**
 * Clean up a phone number by removing all non-numeric characters and the US Country code (if found)
 * This helps with comparing if a phone number is blocked when it is returned formatted differently for some APIs.
 * For example: sometimes it is returned 8015551234 and other times as +18015551234
 */
export function formatPhoneNumberForBlocking(phoneNumber: string): string {
  // remove the US country code if it is at the start
  let cleanedNumber = phoneNumber.replace(/^\+1/, '')
  // remove all non-digit characters
  cleanedNumber = cleanedNumber.replace(/\D+/g, '')
  // remove leading '1' if the number has 11 digits
  if (cleanedNumber.length === 11 && cleanedNumber.startsWith('1')) {
    cleanedNumber = cleanedNumber.substring(1)
  }

  return cleanedNumber
}

/** Returns true if any of the numbers supplied can be blocked and false if none can be blocked or no number was supplied */
export const useCanBlockNumber = (
  phoneNumber: string | string[] | null | undefined,
) => {
  return useMemo(() => {
    if (!phoneNumber) {
      return false
    }
    if (typeof phoneNumber === 'string') {
      return formatPhoneNumberForBlocking(phoneNumber ?? '').length >= 5
    }
    return phoneNumber.some(
      (number) => formatPhoneNumberForBlocking(number).length >= 5,
    )
  }, [phoneNumber])
}

export const useFindBlockedNumber = (
  blockedNumbers?: BlockedNumber[],
  phoneNumber?: string | null,
) => {
  return useMemo(() => {
    const cleanedPhoneNumber = formatPhoneNumberForBlocking(phoneNumber ?? '')
    if (!blockedNumbers || !cleanedPhoneNumber) {
      return undefined
    }

    return blockedNumbers?.find(
      (blocked) => blocked.phoneNumber === cleanedPhoneNumber,
    )
  }, [blockedNumbers, phoneNumber])
}

const logger = new Logger('BlockedNumbers')
let lastLoadedBlockedNumbers = 0

interface useBlockedNumbersProps {
  forceRefresh?: boolean
}

export const useBlockedNumbers = ({
  forceRefresh,
}: useBlockedNumbersProps = {}) => {
  //added force refresh prop
  const { pbxId } = useUserState()
  const [blockedNumbers, setBlockedNumbers] = useAtom(blockedNumbersAtom)
  const { track } = useAnalytics(AnalyticsNamespace.SHARED)
  const {
    confirmModal: [, setConfirmModalState],
  } = useModalState()
  const { t } = useTranslation()

  const pendingUnblockPhoneNumber = useRef<string | undefined>()
  const [isUnblocking, setIsUnblocking] = useState(false)
  const [isBlocking, setIsBlocking] = useState(false)

  const { refetch: refetchBlockedNumbers } = useGetBlockedNumbersQuery({
    pbxId,
    onSuccess: (prevPbxId, apiBlockedNumbers) => {
      if (pbxId !== prevPbxId) return

      const blockedNumbers = apiBlockedNumbers.map((blockedNumber) => ({
        phoneNumber: formatPhoneNumberForBlocking(
          blockedNumber.phone_number.number,
        ),
        id: blockedNumber.id,
      }))
      setBlockedNumbers({ blocked: blockedNumbers })
    },
    onError: (error) => {
      logger.error('Failed to get blocked numbers', error as Error | null)
    },
  })

  const reset = () => {
    setBlockedNumbers({ blocked: null })
    lastLoadedBlockedNumbers = 0
  }

  // #region Unblock number
  const unblockNumberQuery = useUnblockNumberQuery()

  const unblockNumber = (blockedNumberId: number, phoneNumber: string) => {
    track(SharedAnalyticsEvents.UnblockNumber)

    const formattedPhoneNumber = phoneFormatter(phoneNumber)

    setIsUnblocking(true)
    unblockNumberQuery(blockedNumberId, {
      onSuccess: (unblocked) => {
        logger.info(`Unblocked ${formattedPhoneNumber} ${unblocked}`)
        if (!unblocked) {
          logger.info(
            `Reloading blocked numbers, got 404 for a known blocked number id`,
          )
          refetchBlockedNumbers().then(() => {
            pendingUnblockPhoneNumber.current = phoneNumber
          })
        } else {
          setBlockedNumbers((prev) => {
            return {
              blocked: prev.blocked?.filter(
                (blocked) => blocked.id !== blockedNumberId,
              ),
            }
          })
        }
      },
      onError: () => {
        logger.error(`Failed to unblock number ${formattedPhoneNumber}`)
        showErrorToast({
          message: t('confirmBlockNumberModal.toastUnblockError', {
            formattedPhoneNumber,
          }),
        })
      },
      onCompletion: () => {
        setIsUnblocking(false)
      },
    })
  }

  const blockNumberQuery = useBlockNumberQuery({ pbxId })

  const blockNumber = (phoneNumber: string) => {
    const formattedPhoneNumber = phoneFormatter(phoneNumber)
    track(SharedAnalyticsEvents.BlockNumber)
    setIsBlocking(true)
    blockNumberQuery(phoneNumber, {
      onError: () => {
        showErrorToast({
          message: t('confirmBlockNumberModal.toastBlockError', {
            formattedPhoneNumber,
          }),
        })
      },
      onSuccess: (blockedNumber) => {
        if (!blockedNumber) {
          // Someone else blocked the number, we need to reload the blocked numbers to get the new one
          refetchBlockedNumbers()
        } else {
          setBlockedNumbers((prev) => {
            return {
              blocked: append(
                {
                  phoneNumber: formatPhoneNumberForBlocking(
                    blockedNumber.phone_number,
                  ),
                  id: blockedNumber.id,
                },
                prev.blocked ?? [],
              ),
            }
          })
        }
      },
      onCompletion: () => {
        setIsBlocking(false)
      },
    })
  }

  const blockNumberWithConfirmation = (phoneNumber: string) => {
    const formattedPhoneNumber = /^\(\d{3}\) \d{3}-\d{4}$/.test(phoneNumber)
      ? phoneNumber
      : phoneFormatter(phoneNumber)
    setConfirmModalState({
      title: t('confirmBlockNumberModal.title', { formattedPhoneNumber }),
      message: t('confirmBlockNumberModal.message'),
      confirmText: t('confirmBlockNumberModal.confirmText'),
      cancelText: t('confirmBlockNumberModal.cancelText'),
      closeAfterConfirm: true,
      isDestructive: true,
      onConfirm: () => {
        blockNumber(phoneNumber)
      },
    })
  }

  useEffect(() => {
    // There was a problem while unblocking a number (someone unblocked and reblocked it, making the id invalid)
    // so we watch for the reload to finish and unblock the number once it finishes reloading the full list
    if (!pendingUnblockPhoneNumber.current) {
      return
    }

    logger.info(`Unblocking pending ${pendingUnblockPhoneNumber.current}`)
    const blockedNumberToUnblock = blockedNumbers.blocked?.find(
      (blocked) => blocked.phoneNumber === pendingUnblockPhoneNumber.current,
    )
    // We only want to check one time, so we reset the unblockingPhoneNumber here
    pendingUnblockPhoneNumber.current = undefined

    if (!blockedNumberToUnblock) {
      logger.info('Unblocking pending found number is already unblocked')
      return
    }

    logger.info(`Unblock pending found new number to unblock`)
    unblockNumber(blockedNumberToUnblock.id, blockedNumberToUnblock.phoneNumber)
  }, [blockedNumbers])

  useEffect(() => {
    // The first time someone calls this hook, reload the blocked numbers
    const now = new Date().getTime()
    const timeSinceLastLoaded = now - lastLoadedBlockedNumbers
    // Only load once per day. Eventually it would be nice to get this like we do messages instead of polling like this
    if (pbxId && (forceRefresh || timeSinceLastLoaded > 1000 * 60 * 60 * 24)) {
      lastLoadedBlockedNumbers = now
      refetchBlockedNumbers()
    }
  }, [forceRefresh])

  return {
    blockedNumbers: blockedNumbers.blocked ?? undefined,
    isUnblocking,
    isBlocking,
    hasLoadedBlockedNumbers: blockedNumbers.blocked !== null,
    setBlockedNumbers: (blockedNumbers: BlockedNumber[]) => {
      setBlockedNumbers({ blocked: blockedNumbers })
    },
    refetchBlockedNumbers,
    unblockNumber,
    blockNumber,
    blockNumberWithConfirmation,
    reset,
  }
}
